import {action} from "mobx";
import {O, RunInAction} from "web-vcore";
import {store} from "../../../../../Store/index.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {Vec2} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Text, Spinner, Row} from "react-vcomponents";
import {ignore} from "mobx-sync";
import {GetCurrentMapControls} from "../../../../../Store/main/maps.js";

export class ToolState_Pan {
	@O @ignore accessor currentDrag: MapDragInfo<{prevMousePos: Vec2}>|n;
	@action EndCurrentDrag() {
		if (this.currentDrag == null) return;
		this.currentDrag = null;
	}

	touch_touchCount = 0;
	multiTouch_activePinch: {prevDist: number, prevMidpoint: Vec2}|n = null;
}

export class Tool_Pan extends Tool {
	toolType = MapTool.pan;
	get ToolState() { return store.main.maps.tools.toolStates.pan; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const {mapState} = data;
		const mapControls = GetCurrentMapControls();
		const changeZoom = (zoomLevelDelta: number, roundTo: number)=>{
			if (!mapControls) return;
			const newZoomLevel = (mapControls.zoomLevel + zoomLevelDelta).RoundTo(roundTo);
			mapControls.setZoom(newZoomLevel);
		};
		return <Row>
			<Text>Zoom:</Text>
			<Spinner ml={5} style={{width: 60}} min={-Infinity} step={0.5} instant
				value={mapState.camZoom_0ms} onChange={val=>mapControls?.setZoom(val)}/>
			<Button ml={5} p="0px 5px" text="-0.5" onClick={()=>changeZoom(-.5, .5)}/>
			<Button ml={5} p="0px 5px" text="+0.5" onClick={()=>changeZoom(0.5, .5)}/>
			<Button ml={5} p="0px 5px" text="-0.1" onClick={()=>changeZoom(-.1, .1)}/>
			<Button ml={5} p="0px 5px" text="+0.1" onClick={()=>changeZoom(0.1, .1)}/>
		</Row>;
	});

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerDown): ToolInterceptSubresult | void {
		const {event, mousePos, mousePos_world} = data;
		// only start a pan if the mouse-button pressed down was "left" (MapControls itself handles middle-button panning)
		//if (event.button != 0 && event.button != 1) return;
		if (event.button != 0) return;

		const state = this.ToolState;
		if (state.currentDrag == null) {
			RunInAction("Pan.Scene_OnPointerDown", ()=>{
				state.currentDrag = new MapDragInfo({
					button: event.button,
					startPos_mouse: mousePos,
					startPos_world: mousePos_world,
					currentPos_mouse: mousePos,
					currentPos_world: mousePos_world,
					extras: {prevMousePos: mousePos},
				});
			});
			return {absorb: true};
		}
	}
	Document_OnPointerMove(data: ToolData.Document_OnPointerMove): ToolInterceptSubresult | void {
		const {event, mousePos, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag == null) return;
		// suppress single-finger pan while a two-finger pinch is active
		if (state.multiTouch_activePinch != null) return;

		const adjustedPanSpeed = controls.options.panSpeed / controls.ZoomLevel_Transformed;
		const panDelta = new Vec2(
			(mousePos.x - drag.extras.prevMousePos.x) * -1,
			mousePos.y - drag.extras.prevMousePos.y,
		).Times(adjustedPanSpeed);
		controls.ApplyPan(panDelta);

		RunInAction("Pan.Document_OnPointerMove", ()=>{
			drag.currentPos_mouse = mousePos;
			drag.currentPos_world = mousePos_world;
			drag.extras.prevMousePos = mousePos;
		});
		return {absorb: true};
	}
	Document_OnPointerUp(data: ToolData.Document_OnPointerUp): ToolInterceptSubresult | void {
		const {event} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag == null) return;

		if (event.button == drag.button) {
			// pointer-up event fires before touch-up, so if this is a touch event, we infer that true current touch-count is 1 less than the state field currently says
			const newTouchCount = state.touch_touchCount + (data.event.pointerType == "touch" ? -1 : 0);
			// only end single-pointer pan when all fingers are up
			if (newTouchCount <= 0) {
				state.EndCurrentDrag();
				return {absorb: true};
			}
		}
	}

	Scene_OnTouchStart({event}: ToolData.Scene_OnTouchStart): ToolInterceptSubresult | void {
		const state = this.ToolState;
		state.touch_touchCount = event.touches.length;
		if (event.touches.length != 2) return;

		const t0 = event.touches[0];
		const t1 = event.touches[1];
		const dist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
		const midpoint = new Vec2((t0.clientX + t1.clientX) / 2, (t0.clientY + t1.clientY) / 2);
		state.multiTouch_activePinch = {prevDist: dist, prevMidpoint: midpoint};
		return {absorb: true};
	}
	Document_OnTouchMove({event, controls}: ToolData.Document_OnTouchMove): ToolInterceptSubresult | void {
		const state = this.ToolState;
		state.touch_touchCount = event.touches.length; // defensive; might be redundant here (since also in touch start/end), but might help some edge-cases
		if (state.multiTouch_activePinch == null) return;
		if (event.touches.length != 2) return;
		const controls_DOM = controls?.DOM;
		if (controls_DOM == null) return;

		const t0 = event.touches[0];
		const t1 = event.touches[1];
		const newDist = Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
		const newMidpoint = new Vec2((t0.clientX + t1.clientX) / 2, (t0.clientY + t1.clientY) / 2);
		const {prevDist, prevMidpoint} = state.multiTouch_activePinch;

		// Pinch zoom: distance ratio -> zoom-level delta consistent with the logarithmic zoom transform
		if (prevDist > 0 && newDist > 0) {
			const ratio = newDist / prevDist;
			const zoomDelta = Math.log(ratio) / Math.log(controls.zoomLevel_transformBase);
			const worldPosBeforeZoom = controls_DOM.ClientPosToWorldPos(newMidpoint);
			if (controls.options.damping) {
				controls.targetZoomLevel = (controls.targetZoomLevel + zoomDelta).KeepBetween(controls.options.minZoom, controls.options.maxZoom);
			} else {
				controls.zoomLevel = (controls.zoomLevel + zoomDelta).KeepBetween(controls.options.minZoom, controls.options.maxZoom);
				controls.postZoomLevelChanged();
			}
			const worldPosAfterZoom = controls_DOM.ClientPosToWorldPos(newMidpoint);
			const zoomOffset = worldPosBeforeZoom.Minus(worldPosAfterZoom);
			if (controls.options.damping) {
				controls.targetPosition = controls.targetPosition.Plus(zoomOffset);
			} else {
				controls.camera.position.x += zoomOffset.To3JPos().x;
				controls.camera.position.y += zoomOffset.To3JPos().y;
				controls.postCamPosChanged();
			}
		}

		// Two-finger pan: midpoint movement
		const adjustedPanSpeed = controls.options.panSpeed / controls.ZoomLevel_Transformed;
		const panDelta = new Vec2(
			(newMidpoint.x - prevMidpoint.x) * -1,
			newMidpoint.y - prevMidpoint.y,
		).Times(adjustedPanSpeed);
		if (controls.options.damping) {
			controls.targetPosition = controls.targetPosition.Plus(panDelta);
		} else {
			controls.ApplyPan(panDelta);
		}

		state.multiTouch_activePinch = {prevDist: newDist, prevMidpoint: newMidpoint};
		return {absorb: true};
	}
	Document_OnTouchEnd({event}: ToolData.Document_OnTouchEnd): ToolInterceptSubresult | void {
		const state = this.ToolState;
		state.touch_touchCount = event.touches.length;
		if (state.multiTouch_activePinch == null) return;

		// if we drop below 2 touch-points, end the pinch-zoom state
		if (event.touches.length < 2) {
			state.multiTouch_activePinch = null;
			// if a single-pointer pan is also active (in background), re-calibrate its prevMousePos to the remaining finger so the next pointer-move delta is correct
			if (state.currentDrag != null && event.touches.length == 1) {
				const t = event.touches[0];
				state.currentDrag.extras.prevMousePos = new Vec2(t.clientX, t.clientY);
			}
		}
		return {absorb: true};
	}

	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentDrag();
			return {absorb: true};
		}
	}
}